/**
 * Tech Settings → Player Merge.
 *
 * Lists same-name player groups whose seasons (derived from team
 * memberships) are non-overlapping — i.e. they LOOK like one human
 * playing across multiple seasons. The AD reviews each candidate card,
 * picks the canonical row, and confirms a merge. League rule: NEVER
 * auto-merge same-season duplicates (different teenagers).
 *
 * Wires the backend at:
 *   GET  /api/players/{org_id}/duplicate-candidates
 *   POST /api/players/{org_id}/merge   { canonical_id, absorb_ids[] }
 *   GET  /api/players/{org_id}/merge-log
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Users, Loader2, RefreshCw, GitMerge, Check, AlertTriangle, History,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PlayerMergeSection({ orgId }) {
  const { isDark } = useTheme();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(null); // group key
  const [canonicalById, setCanonicalById] = useState({}); // groupKey -> playerId
  const [log, setLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [err, setErr] = useState(null);
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const text = isDark ? 'text-slate-100' : 'text-slate-900';
  const cardBg = isDark ? 'bg-slate-800/40 border-slate-700' : 'bg-white border-slate-200';

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true); setErr(null);
    try {
      const [c, l] = await Promise.all([
        axios.get(`${API}/api/players/${orgId}/duplicate-candidates`,
          { withCredentials: true }),
        axios.get(`${API}/api/players/${orgId}/merge-log`,
          { withCredentials: true }).catch(() => ({ data: { data: [] } })),
      ]);
      const data = c.data?.data || [];
      setCandidates(data);
      // Default each group's canonical to the row with the most seasons.
      const defaults = {};
      data.forEach((g, idx) => {
        const key = `${g.last_name}__${g.first_name}__${idx}`;
        const sorted = [...g.rows].sort(
          (a, b) => (b.seasons?.length || 0) - (a.seasons?.length || 0));
        defaults[key] = sorted[0]?.id;
      });
      setCanonicalById(defaults);
      setLog(l.data?.data || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const onMerge = async (groupKey, group) => {
    const canonicalId = canonicalById[groupKey];
    if (!canonicalId) { alert('Pick a canonical row first'); return; }
    const absorbIds = group.rows.filter(r => r.id !== canonicalId).map(r => r.id);
    if (!absorbIds.length) { alert('Nothing to merge'); return; }
    const summary = group.rows.find(r => r.id === canonicalId);
    if (!confirm(
      `Merge ${absorbIds.length} duplicate record(s) for ${group.first_name} ${group.last_name} ` +
      `into canonical record ${summary?.slug || canonicalId.slice(0, 8)}?\n\n` +
      `This will: \n` +
      ` • Copy all team memberships into the canonical row\n` +
      ` • Re-point roster/registration/availability FKs\n` +
      ` • Delete the absorbed rows\n\n` +
      `League rule: only do this if you're CONFIDENT they're the same person.`)) return;
    setMerging(groupKey);
    try {
      await axios.post(`${API}/api/players/${orgId}/merge`,
        { canonical_id: canonicalId, absorb_ids: absorbIds },
        { withCredentials: true });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    } finally { setMerging(null); }
  };

  return (
    <div className="space-y-4" data-testid="player-merge-section">
      <div className="flex items-center justify-between">
        <div>
          <div className={`text-sm font-semibold flex items-center gap-2 ${text}`}>
            <GitMerge className="h-4 w-4 text-teal-400"/>
            AD-Confirmed Player Merges
          </div>
          <div className={`text-xs ${muted} mt-0.5`}>
            Records that share a first/last name and have <strong>non-overlapping seasons</strong>.
            League rule: same-season duplicates are different teenagers — never merged.
            Cross-season merges require AD confirmation.
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => setShowLog(s => !s)} data-testid="toggle-merge-log">
            <History className="h-3 w-3 mr-1"/> Log ({log.length})
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs"
                  onClick={load} disabled={loading} data-testid="player-merge-refresh">
            {loading ? <Loader2 className="h-3 w-3 animate-spin"/> : <RefreshCw className="h-3 w-3"/>}
          </Button>
        </div>
      </div>

      {err && (
        <div className={`text-xs px-3 py-2 rounded border flex items-center gap-2
          ${isDark ? 'bg-rose-900/20 border-rose-700/40 text-rose-300' : 'bg-rose-50 border-rose-200 text-rose-700'}`}>
          <AlertTriangle className="h-3.5 w-3.5"/> {err}
        </div>
      )}

      {showLog && (
        <Card className={cardBg}>
          <CardContent className="p-3">
            <div className={`text-xs font-semibold mb-2 ${text}`}>Recent merges</div>
            {log.length === 0 ? (
              <div className={`text-xs text-center py-4 ${muted}`}>No merges performed yet.</div>
            ) : (
              <div className="space-y-1">
                {log.slice(0, 20).map(row => (
                  <div key={row.canonical_id + row.at}
                       className={`flex items-center justify-between gap-3 text-xs px-2 py-1.5 rounded
                         ${isDark ? 'bg-slate-900/40' : 'bg-slate-50'}`}>
                    <div className={`font-mono ${muted}`}>{new Date(row.at).toLocaleString()}</div>
                    <div className={text}>
                      <span className="font-mono">{(row.canonical_id || '').slice(0, 8)}</span>
                      <span className="mx-1 opacity-60">←</span>
                      {row.absorb_ids?.length || 0} absorbed
                    </div>
                    <div className={muted}>by {row.by || 'unknown'}</div>
                    <Badge variant="outline" className="text-[10px]">
                      {row.fk_repointed_count || 0} FK repointed
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {loading && candidates.length === 0 ? (
        <div className={`text-center text-xs py-6 ${muted}`}>
          <Loader2 className="h-4 w-4 animate-spin inline mr-1"/> Scanning…
        </div>
      ) : candidates.length === 0 ? (
        <Card className={cardBg}>
          <CardContent className="p-6 text-center">
            <Check className={`h-6 w-6 mx-auto mb-2 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}/>
            <div className={`text-sm ${text}`}>No duplicate candidates detected.</div>
            <div className={`text-xs mt-1 ${muted}`}>
              All same-name records either share a season (different humans, by rule)
              or already coexist as the canonical entry. Refresh after the next WP enrichment run.
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {candidates.map((group, idx) => {
            const key = `${group.last_name}__${group.first_name}__${idx}`;
            const canonical = canonicalById[key];
            return (
              <Card key={key} className={cardBg} data-testid={`merge-candidate-${idx}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className={`text-sm font-semibold flex items-center gap-2 ${text}`}>
                      <Users className="h-3.5 w-3.5 text-teal-400"/>
                      {group.first_name} {group.last_name}
                      <Badge variant="outline" className="text-[10px]">{group.rows.length} records</Badge>
                    </div>
                    <Button size="sm" onClick={() => onMerge(key, group)}
                            disabled={merging === key || !canonical}
                            className="h-7 text-xs bg-teal-600 hover:bg-teal-700"
                            data-testid={`merge-btn-${idx}`}>
                      {merging === key ? <Loader2 className="h-3 w-3 mr-1 animate-spin"/>
                                       : <GitMerge className="h-3 w-3 mr-1"/>}
                      Confirm Merge
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {group.rows.map(row => (
                      <label key={row.id}
                             className={`flex items-start gap-3 px-2 py-2 rounded cursor-pointer
                               text-xs border ${canonical === row.id
                                 ? (isDark ? 'border-teal-500/60 bg-teal-900/15' : 'border-teal-400 bg-teal-50')
                                 : (isDark ? 'border-slate-700 hover:border-slate-600' : 'border-slate-200 hover:border-slate-300')}`}>
                        <input type="radio" name={key}
                               className="mt-0.5 accent-teal-500"
                               checked={canonical === row.id}
                               onChange={() => setCanonicalById(s => ({ ...s, [key]: row.id }))}
                               data-testid={`canonical-pick-${idx}-${row.id}`}/>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`font-mono ${text}`}>{row.slug || row.id?.slice(0, 8)}</span>
                            {row.wp_player_id && (
                              <Badge variant="outline" className="text-[10px]">
                                WP #{row.wp_player_id}
                              </Badge>
                            )}
                            {canonical === row.id && (
                              <Badge className="text-[10px] bg-teal-600">Canonical</Badge>
                            )}
                          </div>
                          <div className={`mt-1 text-[11px] ${muted}`}>
                            Seasons: {row.seasons?.length ? row.seasons.join(', ') : '—'}
                          </div>
                          {row.teams?.length > 0 && (
                            <div className={`text-[11px] mt-0.5 ${muted}`}>
                              Teams: {row.teams.slice(0, 4).join(' · ')}
                              {row.teams.length > 4 ? ` · +${row.teams.length - 4}` : ''}
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
